import { Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { TenantContextService } from '../core/tenant-context.service';

@Injectable({
  providedIn: 'root'
})
export class TenantRouterService {

  constructor(
    private router: Router,
    private tenantContext: TenantContextService
  ) {}

  /**
   * Navigate to a tenant-scoped route
   * Usage:
   *  navigate('manager-dashboard')
   *  navigate(['team', teamId])
   */
  navigate(path: string | any[]): void {
    const tenant = this.resolveTenant();

    if (!tenant) {
      this.redirectToTenantLogin();
      return;
    }

    const segments = Array.isArray(path) ? path : [path];
    this.router.navigate(['/', tenant, ...segments]);
  }

  /**
   * Navigate by URL inside tenant scope
   * Usage:
   *  navigateByUrl('manager-dashboard')
   *  navigateByUrl('team/123')
   */
  navigateByUrl(url: string): void {
    const tenant = this.resolveTenant();

    if (!tenant) {
      this.redirectToTenantLogin();
      return;
    }

    const cleanUrl = url.startsWith('/') ? url.slice(1) : url;
    this.router.navigateByUrl(`/${tenant}/${cleanUrl}`);
  }

  /**
   * Create UrlTree (useful in Guards & Resolvers)
   */
  createUrlTree(path: string | any[]): UrlTree {
    const tenant = this.resolveTenant();

    if (!tenant) {
      return this.router.parseUrl('/login');
    }

    const segments = Array.isArray(path) ? path : [path];
    return this.router.createUrlTree(['/', tenant, ...segments]);
  }

  /**
   * Redirect to tenant login (safe fallback)
   */
  redirectToTenantLogin(): void {
    const tenant = this.tenantContext.getTenantSlug();

    if (tenant) {
      this.router.navigateByUrl(`/${tenant}/login`);
    } else {
      // Absolute fallback – no tenant known
      this.router.navigateByUrl('/login');
    }
  }

  /**
   * Logout-safe redirect
   */
  logoutRedirect(): void {
  const tenant = this.tenantContext.getTenantSlug();

  if (tenant) {
    this.router.navigateByUrl(`/${tenant}/login`);
  } else {
    this.router.navigateByUrl('/login');
  }
}


  /**
   * Internal tenant resolver
   * Ensures empty / invalid values are not used
   */
  private resolveTenant(): string | null {
    const tenant = this.tenantContext.getTenantSlug();

    if (!tenant || tenant.trim().length === 0) {
      console.warn('[TenantRouter] Tenant not resolved');
      return null;
    }

    return tenant;
  }
}
